import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';

/**
 * FADE-IN ANIMATION
 */
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

/**
 * STYLED COMPONENTS
 */

// Outer container with fade-in effect
const HeatmapContainer = styled.div`
  margin: 1rem 0;
  overflow-x: auto;
  animation: ${fadeIn} 0.8s ease-in;
`;

// Table layout is "fixed" to enforce uniform column widths
const StyledTable = styled.table`
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed; /* important for consistent column widths */
  min-width: 600px; /* can adjust as needed for narrower or wider default */
  font-family: 'Raleway', sans-serif;
  color: #333;
`;

const HeaderRow = styled.tr`
  background-color: #fff;
`;

/**
 * We'll set a fixed width and rotate the text within.
 * Using 'position: relative; vertical-align: bottom;' allows
 * the text to appear at the bottom of the cell when rotated.
 */
const HeaderCell = styled.th`
  width: 70px; /* All columns the same width */
  height: 120px; /* Enough height to accommodate slanted text */
  position: relative;
  vertical-align: bottom;
  border-bottom: 2px solid #ececec;
  text-align: center;

  > span {
    display: inline-block;
    transform: rotate(-45deg);
    transform-origin: left bottom;
    white-space: nowrap;
    font-weight: 600;
    color: #555;
  }
`;

// A blank cell in the top-left corner
const BlankHeaderCell = styled.th`
  width: 100px; /* row title column width */
  border-bottom: 2px solid #ececec;
  background: #fff;
`;

const RowHeaderCell = styled.td`
  width: 100px; /* same as BlankHeaderCell for consistency */
  padding: 0.75rem;
  font-weight: 500;
  color: #444;
  background: #fff;
  border-bottom: 1px solid #f1f1f1;
  text-align: left;
`;

const ScoreCell = styled.td`
  text-align: center;
  padding: 0.75rem;
  border-bottom: 1px solid #f1f1f1;
  background-color: ${({ bg }) => bg};
  transition: background-color 0.3s ease;
  color: #333;
  font-weight: 500;
`;

/**
 * getCellColor: produce a more pronounced color contrast (0–100).
 * Adjust thresholds/colors to taste.
 */
function getCellColor(score) {
  if (score === null || score === undefined) {
    return '#ffffff'; // No data
  }
  if (score >= 90) {
    return '#8cdba1'; // deeper green
  } else if (score >= 80) {
    return '#b8e3ce'; // medium green
  } else if (score >= 70) {
    return '#f5f5f5'; // neutral gray
  } else if (score >= 60) {
    return '#ffdcdc'; // light pink
  } else {
    return '#ffc2c2'; // deeper pink
  }
}

/**
 * BetterHeatmap
 * Expects data in the form:
 * {
 *   rowTitles: ["Engagement", "Retention", ...],
 *   columnTitles: ["Overall 440", "Chicago 25", "Madrid 5", ...],
 *   scores: [
 *       [8.5, 7.0, 7.1], // factor1 (0–10 scale, we convert to 0–100)
 *       [8.3, 7.6, 7.0],
 *       ...
 *   ]
 * }
 * 
 * We'll:
 *   - Assign safe defaults if data is missing.
 *   - Convert scores to base 100 (round).
 *   - Sort rows & columns by their average (descending).
 *   - Render a table with slanted column headers and uniform column widths.
 */
export default function BetterHeatmap({ data }) {
  // 1) Safely destructure or set defaults
  const rowTitles = data?.rowTitles || [];
  const columnTitles = data?.columnTitles || [];
  const scores = data?.scores || [];

  // 2) useMemo is always called (not behind a conditional return)
  const { sortedRowTitles, sortedColumnTitles, finalScores } = useMemo(() => {
    // If no data, just return empty structures
    if (!rowTitles.length || !columnTitles.length || !scores.length) {
      return {
        sortedRowTitles: [],
        sortedColumnTitles: [],
        finalScores: [],
      };
    }

    // 1) Scale to 0–100
    const scaledScores = scores.map(row =>
      row.map(val => {
        if (val == null) return null;
        return Math.round(val * 10); // e.g. 8.3 => 83
      })
    );

    // 2) Row averages -> sort row indices descending
    const rowAverages = scaledScores.map(row => {
      const valid = row.filter(x => x !== null);
      if (!valid.length) return 0;
      return valid.reduce((a, b) => a + b, 0) / valid.length;
    });
    const rowIndices = rowAverages
      .map((avg, idx) => [idx, avg])
      .sort((a, b) => b[1] - a[1])
      .map(([idx]) => idx);

    // 3) Column averages -> sort column indices descending
    const numRows = scaledScores.length;
    const numCols = columnTitles.length;

    const colAverages = [];
    for (let c = 0; c < numCols; c++) {
      let sum = 0;
      let count = 0;
      for (let r = 0; r < numRows; r++) {
        const val = scaledScores[r][c];
        if (val !== null && val !== undefined) {
          sum += val;
          count++;
        }
      }
      colAverages.push(count ? sum / count : 0);
    }
    const colIndices = colAverages
      .map((avg, idx) => [idx, avg])
      .sort((a, b) => b[1] - a[1])
      .map(([idx]) => idx);

    // 4) Re-map rowTitles, columnTitles, and scaledScores
    const sortedRowTitles = rowIndices.map(ri => rowTitles[ri]);
    const sortedColumnTitles = colIndices.map(ci => columnTitles[ci]);
    const finalScores = rowIndices.map(ri =>
      colIndices.map(ci => scaledScores[ri][ci])
    );

    return {
      sortedRowTitles,
      sortedColumnTitles,
      finalScores,
    };
  }, [rowTitles, columnTitles, scores]);

  // 3) If we still have no finalScores, we display a fallback
  if (!finalScores.length) {
    return <div>No heatmap data available.</div>;
  }

  // 4) Return the final table
  return (
    <HeatmapContainer>
      <StyledTable>
        <thead>
          <HeaderRow>
            {/* Blank top-left corner */}
            <BlankHeaderCell />
            {/* Sorted column titles, slanted */}
            {sortedColumnTitles.map((colTitle, index) => (
              <HeaderCell key={`head-${index}`}>
                <span>{colTitle}</span>
              </HeaderCell>
            ))}
          </HeaderRow>
        </thead>
        <tbody>
          {sortedRowTitles.map((rowTitle, rowIndex) => (
            <tr key={`row-${rowIndex}`}>
              {/* Factor name on the left */}
              <RowHeaderCell>{rowTitle}</RowHeaderCell>
              {/* Scores in sorted column order */}
              {finalScores[rowIndex].map((score, colIndex) => {
                const bgColor = getCellColor(score);
                return (
                  <ScoreCell key={`score-${rowIndex}-${colIndex}`} bg={bgColor}>
                    {score != null ? score : '—'}
                  </ScoreCell>
                );
              })}
            </tr>
          ))}
        </tbody>
      </StyledTable>
    </HeatmapContainer>
  );
}
